import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";

import { ReactComponent as ExportIcon } from "../../../../../assets/images/export.svg";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiDateFieldFormController,
  MuiLink,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  RowProps,
  StatusBadge,
} from "../../../../../components";
import {
  MAX_LENGTH_50,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../../helpers/constants";
import {
  filterListByFilterKeyValue,
  getUniqueFilters,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { mapOutReachReports } from "../../../helper";
import {
  OutreachReportSummaryRow,
  OutreachReportKey,
  OutreachReportDropdownTypes,
} from "../../../modelsAndSchemas/models";
import { reportsResultsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import { OutreachReportSummary } from "../../../../../api/services/reports/models";

const pageId = `${PAGE_IDS.REPORTS}-list`;

const {
  programKey,
  clientName,
  participantName,
  marketForDeliveryOffice,
  deliveryOffice,
  programTemplate,
  outreachEligibleDate,
  nextContactDate,
  lastActivity,
  programStatus,
  programStatusDisplayValue,
  mustStartByDate,
  stateRegionProvince,
  contactWithin24Hrs,
  bestMatchPreference,
  alternateDisplayName,
  registrationCode,
  registrationStatusNext,
  registrationStatusNextDisplayValue,
  notificationSupportDate,
  primaryEmailId,
  alternateEmailId,
  primaryPhoneNumber,
  alternatePhoneNumber,
  appendedOtherPhone,
  registrationEmailId,
  releaseDate,
  notificationDate,
  nisId,
} = OutreachReportKey;

interface OutreachReportResultProps {
  outReachReports: OutreachReportSummary[];
  isFilteredOutReachReport: boolean;
  onExport: () => void;
}
export const OutreachReportResult = ({
  outReachReports,
  isFilteredOutReachReport = false,
  onExport,
}: OutreachReportResultProps) => {
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const {
    commonLabels,
    useFetchLabel,
    fetchValueByKeyOfDropdown,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [filteredReports, setFilteredReports] = useState<
    OutreachReportSummaryRow[]
  >([]);

  const [uniqueFiltersOptionObj, setUniqueFiltersOptionObj] =
    useState<OutreachReportDropdownTypes>({
      programStatus: [],
      contactWithin24Hrs: [],
      registrationStatusNext: [],
    });

  const { control, getValues, reset } = useForm({
    defaultValues: {
      [programKey]: "",
      [clientName]: "",
      [participantName]: "",
      [marketForDeliveryOffice]: "",
      [deliveryOffice]: "",
      [programTemplate]: "",
      [outreachEligibleDate]: "",
      [nextContactDate]: "",
      [lastActivity]: "",
      [programStatusDisplayValue]: "",
      [mustStartByDate]: "",
      [stateRegionProvince]: "",
      [contactWithin24Hrs]: "",
      [bestMatchPreference]: "",
      [alternateDisplayName]: "",
      [registrationCode]: "",
      [registrationStatusNextDisplayValue]: "",
      [notificationSupportDate]: "",
      [primaryEmailId]: "",
      [alternateEmailId]: "",
      [primaryPhoneNumber]: "",
      [alternatePhoneNumber]: "",
      [appendedOtherPhone]: "",
      [registrationEmailId]: "",
      [releaseDate]: "",
      [notificationDate]: "",
      [nisId]: "",
    },
  });

  //   watch entire form state
  const filters = useWatch({ control });

  const getLabel = useFetchLabel(reportsResultsSiteCoreKeys.header);

  const getValueByKeyOfDropdown = (listKey: string, key: string) =>
    fetchValueByKeyOfDropdown(reportsResultsSiteCoreKeys.header, listKey, key);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const formattedOutReachReport = useMemo(() => {
    reset();
    setFilteredReports([]);
    if (!outReachReports?.length) return [];
    return mapOutReachReports(
      outReachReports,
      getValueOrBlank(userCountryDateFormat),
      getValueByKeyOfDropdown,
      commonLabels,
    );
  }, [outReachReports, siteCoreLoading]);

  useEffect(() => {
    const uniqueProgramStatus = getUniqueFilters(
      formattedOutReachReport,
      "programStatusDisplayValue",
    );
    const uniqueContactWithin24Hrs = getUniqueFilters(
      formattedOutReachReport,
      "contactWithin24Hrs",
    );
    const uniqueRegistrationStatusNext = getUniqueFilters(
      formattedOutReachReport,
      "registrationStatusNextDisplayValue",
    );
    setUniqueFiltersOptionObj({
      programStatus: uniqueProgramStatus,
      contactWithin24Hrs: uniqueContactWithin24Hrs,
      registrationStatusNext: uniqueRegistrationStatusNext,
    });
  }, [formattedOutReachReport]);

  const appliedFilters = () => {
    if (!formattedOutReachReport.length) return;
    const filterList = filterListByFilterKeyValue(
      formattedOutReachReport,
      filters,
    ) as OutreachReportSummaryRow[];
    setFilteredReports(filterList);
    resetPagination();
  };

  useEffect(() => {
    appliedFilters();
  }, [filters]);

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const renderName = (companyName: string) => {
    return (
      <MuiBox data-testid="link">
        <MuiEllipseLabel
          text={companyName}
          labelTypoVariant="h3"
          placement="bottom-start"
        />
      </MuiBox>
    );
  };

  const columns: Column[] = [
    {
      id: programKey,
      label: getLabel(reportsResultsSiteCoreKeys.programKeyTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          href={`${PAGE_URLS.VIEW_PROGRAM}/${row["programId"]}`}
          isOpenNewTab
        >
          {renderName(row[programKey])}
        </MuiLink>
      ),
    },
    {
      id: clientName,
      label: getLabel(reportsResultsSiteCoreKeys.clientName),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          href={`${PAGE_URLS.VIEW_CLIENT}/${row["clientId"]}`}
          isOpenNewTab
        >
          {renderName(row[clientName])}
        </MuiLink>
      ),
    },
    {
      id: participantName,
      label: getLabel(
        reportsResultsSiteCoreKeys.participantNameResultTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: marketForDeliveryOffice,
      label: getLabel(reportsResultsSiteCoreKeys.marketOfDeliveryOffice),
      isSortVisible: true,
    },
    {
      id: deliveryOffice,
      label: getLabel(reportsResultsSiteCoreKeys.deliveryOfficeTableColumn),
      isSortVisible: true,
    },
    {
      id: programTemplate,
      label: getLabel(
        reportsResultsSiteCoreKeys.programTemplateResultTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: outreachEligibleDate,
      label: getLabel(
        reportsResultsSiteCoreKeys.outreachEligibleDateTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: nextContactDate,
      label: getLabel(reportsResultsSiteCoreKeys.nextContactDateTableColumn),
      isSortVisible: true,
    },
    {
      id: lastActivity,
      label: getLabel(reportsResultsSiteCoreKeys.lastActivityDateTableColumn),
      isSortVisible: true,
    },
    {
      id: programStatusDisplayValue,
      label: getLabel(
        reportsResultsSiteCoreKeys.programStatusResultTableColumn,
      ),
      render: (row: RowProps) => (
        <StatusBadge
          status={row[programStatus]}
          label={row[programStatusDisplayValue]}
        />
      ),
      isSortVisible: true,
    },
    {
      id: mustStartByDate,
      label: getLabel(reportsResultsSiteCoreKeys.mustStartByDateTableColumn),
      isSortVisible: true,
    },
    {
      id: stateRegionProvince,
      label: getLabel(
        reportsResultsSiteCoreKeys.stateRegionProvinceTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: contactWithin24Hrs,
      label: getLabel(reportsResultsSiteCoreKeys.contactWithin24HrsTableColumn),
      isSortVisible: true,
    },
    {
      id: bestMatchPreference,
      label: getLabel(
        reportsResultsSiteCoreKeys.bestMatchPreferenceTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: alternateDisplayName,
      label: getLabel(reportsResultsSiteCoreKeys.alternateProgramTemplateName),
      isSortVisible: true,
    },
    {
      id: registrationCode,
      label: getLabel(reportsResultsSiteCoreKeys.registrationCodeTableColumn),
      isSortVisible: true,
    },
    {
      id: registrationStatusNextDisplayValue,
      label: getLabel(
        reportsResultsSiteCoreKeys.registrationStatusNEXTTableColumn,
      ),
      render: (row: RowProps) => (
        <StatusBadge
          status={row[registrationStatusNext]}
          label={row[registrationStatusNextDisplayValue]}
        />
      ),
      isSortVisible: true,
    },
    {
      id: notificationSupportDate,
      label: getLabel(
        reportsResultsSiteCoreKeys.notificationSupportDateTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: primaryEmailId,
      label: getLabel(reportsResultsSiteCoreKeys.primaryEmailIDTableColumn),
      isSortVisible: true,
    },
    {
      id: alternateEmailId,
      label: getLabel(reportsResultsSiteCoreKeys.alternateEmailIDTableColumn),
      isSortVisible: true,
    },
    {
      id: primaryPhoneNumber,
      label: getLabel(reportsResultsSiteCoreKeys.primaryPhoneNumberTableColumn),
      isSortVisible: true,
    },
    {
      id: alternatePhoneNumber,
      label: getLabel(
        reportsResultsSiteCoreKeys.alternatePhoneNumberTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: appendedOtherPhone,
      label: getLabel(reportsResultsSiteCoreKeys.appendedOtherPhoneTableColumn),
      isSortVisible: true,
    },
    {
      id: registrationEmailId,
      label: getLabel(
        reportsResultsSiteCoreKeys.registrationEmailIdNextTableColumn,
      ),
      isSortVisible: true,
    },

    {
      id: releaseDate,
      label: getLabel(reportsResultsSiteCoreKeys.releaseDateTableColumn),
      isSortVisible: true,
    },
    {
      id: notificationDate,
      label: getLabel(reportsResultsSiteCoreKeys.notificationDateTableColumn),
      isSortVisible: true,
    },
    {
      id: nisId,
      label: getLabel(reportsResultsSiteCoreKeys.nisIdTableColumn),
      isSortVisible: true,
    },
  ];

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextFieldWithSearchIcon
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          searchARIALabel={commonLabels.search}
          data-testid={`${name}-search-field`}
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };
  const tableHeaderForm: React.JSX.Element[] = [
    renderFilterTextField(programKey),
    renderFilterTextField(clientName),
    renderFilterTextField(participantName),
    renderFilterTextField(marketForDeliveryOffice),
    renderFilterTextField(deliveryOffice),
    renderFilterTextField(programTemplate),
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${outreachEligibleDate}`}
      id={`${pageId}-${outreachEligibleDate}`}
      name={outreachEligibleDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(outreachEligibleDate))}
    />,
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${nextContactDate}`}
      id={`${pageId}-${nextContactDate}`}
      name={nextContactDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(nextContactDate))}
    />,
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${lastActivity}`}
      id={`${pageId}-${lastActivity}`}
      name={lastActivity}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(lastActivity))}
    />,
    <FormController
      control={control}
      name={programStatusDisplayValue}
      key={programStatusDisplayValue}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersOptionObj.programStatus}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${programStatusDisplayValue}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${mustStartByDate}`}
      id={`${pageId}-${mustStartByDate}`}
      name={mustStartByDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(mustStartByDate))}
    />,

    renderFilterTextField(stateRegionProvince),
    <FormController
      control={control}
      name={contactWithin24Hrs}
      key={contactWithin24Hrs}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersOptionObj.contactWithin24Hrs}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${contactWithin24Hrs}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFilterTextField(bestMatchPreference),
    renderFilterTextField(alternateDisplayName),
    renderFilterTextField(registrationCode),
    <FormController
      control={control}
      name={registrationStatusNext}
      key={registrationStatusNext}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersOptionObj.registrationStatusNext}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${registrationStatusNext}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${notificationSupportDate}`}
      id={`${pageId}-${notificationSupportDate}`}
      name={notificationSupportDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(notificationSupportDate))}
    />,
    renderFilterTextField(primaryEmailId),
    renderFilterTextField(alternateEmailId),
    renderFilterTextField(primaryPhoneNumber),
    renderFilterTextField(alternatePhoneNumber),
    renderFilterTextField(appendedOtherPhone),
    renderFilterTextField(registrationEmailId),
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${notificationDate}`}
      id={`${pageId}-${notificationDate}`}
      name={notificationDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(notificationDate))}
    />,
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${releaseDate}`}
      id={`${pageId}-${releaseDate}`}
      name={releaseDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(releaseDate))}
    />,
    renderFilterTextField(nisId),
  ];

  const tableHeaderLabel = isFilteredOutReachReport
    ? fetchComponentTitle(reportsResultsSiteCoreKeys.header)
    : getLabel(reportsResultsSiteCoreKeys.allProgramsHeader);

  return (
    <MuiBox className="result-container" data-testid="outreach-report-result">
      <MuiBox className="result-header">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {tableHeaderLabel}
        </MuiTypography>
        {outReachReports.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="header-count">
            {outReachReports.length}
          </MuiTypography>
        )}
        <MuiBox textAlign="right" flexGrow={1}>
          <MuiButtonTertiary onClick={onExport}>
            <ExportIcon />
            {commonLabels.export}
          </MuiButtonTertiary>
        </MuiBox>
      </MuiBox>

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        emptyMessage={commonLabels.searchCriteria}
        tableResults={filteredReports as unknown as Record<string, ReactNode>[]}
        noDataInApi={outReachReports.length === 0}
        noResultMessage={commonLabels.noRecordsFound}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
    </MuiBox>
  );
};
